import React from "react";
import { Badge, Button, Dropdown, Icon, type IconStrokeGlyph } from "@gusarov-studio/rubik-ui";
import type { ToolValues } from "../schemas/ToolSchema";

type ButtonProps = React.ComponentProps<typeof Button>;

interface ToolsButtonProps
  extends Pick<ButtonProps, "size" | "appearance" | "shape" | "className"> {
  tools: ToolValues[];
  onAdd: () => void;
  onEdit: (index: number) => void;
  /** Shown before the label. */
  icon?: IconStrokeGlyph;
  /** Show the count badge with no tools too ("Tools 0" rather than "Tools"). */
  showZeroCount?: boolean;
  /** Overrides for the count badge (variant, appearance, ...). */
  badgeProps?: Omit<React.ComponentProps<typeof Badge>, "children">;
}

/**
 * Entry point for a prompt's or a chat's tools: adds the first one directly,
 * then turns into a list that opens each tool for editing. Deleting lives on
 * the tool dialog footer rather than on each row.
 */
const ToolsButton: React.FC<ToolsButtonProps> = ({
  tools,
  onAdd,
  onEdit,
  icon,
  showZeroCount = false,
  badgeProps,
  size = "36",
  appearance = "outline",
  shape,
  className,
}) => {
  "use memo";

  const trigger = (
    <Button
      variant="secondary"
      size={size}
      appearance={appearance}
      shape={shape}
      className={className}
      onClick={tools.length === 0 ? onAdd : undefined}
    >
      {icon && (
        <Icon
          glyph={icon}
          color="inherit"
          size={20}
          strokeWidth={1.5}
        />
      )}
      Tools
      {(tools.length > 0 || showZeroCount) && (
        <Badge
          size={20}
          variant="secondary"
          shape="pill"
          {...badgeProps}
        >
          {tools.length}
        </Badge>
      )}
    </Button>
  );

  if (tools.length === 0) {
    return trigger;
  }

  return (
    <Dropdown.Root>
      <Dropdown.Trigger asChild>{trigger}</Dropdown.Trigger>
      <Dropdown.Menu
        className="w-[200px]!"
        side="top"
        sideOffset={8}
        align="start"
      >
        {tools.map((tool, index) => (
          <Dropdown.Item
            key={tool.id}
            onSelect={() => onEdit(index)}
            prefix={
              <Icon
                glyph="code-circle-02"
                size={16}
              />
            }
          >
            {tool.name}
          </Dropdown.Item>
        ))}
        <Dropdown.Divider />
        <Dropdown.Item
          onSelect={onAdd}
          prefix={
            <Icon
              glyph="plus"
              size={16}
            />
          }
        >
          Add custom tool...
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown.Root>
  );
};

export { ToolsButton, type ToolsButtonProps };
